/**
 * FormAccessContent
 *
 * Body of `FormAccessModal`: the security-group assignment table and the
 * admin gate. Split out of the modal shell so `destroyOnHidden` actually
 * unmounts it — the shell keeps `open` so antd owns the transition, while
 * everything that subscribes to a store lives here and exists only while the
 * dialog is on screen.
 *
 * Mount implies open, so `useFormAccess` needs no `enabled` gate: unmounting
 * runs its effect cleanup and disconnects the shared `securityGroups` store.
 * The form itself is read at click time inside `applyAccessChange`, never
 * subscribed to.
 */

import { FC } from 'react';
import { Select, Space, Typography, Alert, Modal } from 'antd';
import ResponsiveTable from '@/components/atoms/ResponsiveTable';
import { snapshot } from 'valtio';
import { useFormAccess, GroupAccessItem, AccessGroupType } from '../../use/useFormAccess';
import { formStore } from '../../store';
import FlexCol from '../atoms/FlexCol';

const { Text } = Typography;

const ACCESS_TYPE_LABELS: Record<AccessGroupType, string> = {
    readGroupIds: 'Reader',
    writeGroupIds: 'Writer',
    adminGroupIds: 'Admin',
};

const FormAccessContent: FC = () => {
    const [modal, contextHolder] = Modal.useModal();
    const {
        groupAccessList,
        isAdmin,
        loading,
    } = useFormAccess();

    const applyAccessChange = async (groupId: string, accessType: AccessGroupType | null) => {
        if (!isAdmin) return;
        const live = snapshot(formStore.state);
        if (!live.formId) return;

        const current = live.form.data?.accessGroups ?? {};
        const readGroupIds = (current.readGroupIds ?? []).filter((g: string) => g !== groupId);
        const writeGroupIds = (current.writeGroupIds ?? []).filter((g: string) => g !== groupId);
        const adminGroupIds = (current.adminGroupIds ?? []).filter((g: string) => g !== groupId);

        if (accessType === 'readGroupIds') readGroupIds.push(groupId);
        else if (accessType === 'writeGroupIds') writeGroupIds.push(groupId);
        else if (accessType === 'adminGroupIds') adminGroupIds.push(groupId);

        const nextAccessGroups = { readGroupIds, writeGroupIds, adminGroupIds };
        await formStore.getFormPersist()?.setMerge({ accessGroups: nextAccessGroups }, { debounce: false });
    };

    const handleAccessChange = async (groupId: string, accessType: AccessGroupType | null) => {
        const currentAccess = groupAccessList.find(g => g.docId === groupId)?.accessType;
        const groupName = groupAccessList.find(g => g.docId === groupId)?.name;

        if (currentAccess === 'adminGroupIds' && accessType !== 'adminGroupIds') {
            modal.confirm({
                title: 'Remove Admin Access',
                content: (
                    <Typography.Text>
                        Are you sure you want to change access for group{' '}
                        <Typography.Text strong>{groupName}</Typography.Text>{' '}
                        from Admin to {accessType ? ACCESS_TYPE_LABELS[accessType] : 'No access'}?
                    </Typography.Text>
                ),
                onOk: async () => {
                    await applyAccessChange(groupId, accessType);
                }
            });
            return;
        }

        await applyAccessChange(groupId, accessType);
    };

    const columns = [
        {
            title: 'Group',
            dataIndex: 'name',
            key: 'name',
            render: (name: string, record: GroupAccessItem) => (
                <Space>
                    <Text>{name}</Text>
                    {!record.accessType && (
                        <Text type="secondary" italic>
                            (No access)
                        </Text>
                    )}
                </Space>
            ),
        },
        {
            title: 'Access Level',
            dataIndex: 'accessType',
            key: 'accessType',
            render: (accessType: AccessGroupType | null, record: GroupAccessItem) => (
                <Select
                    size='small'
                    variant='borderless'
                    value={accessType}
                    onChange={(newAccessType) => handleAccessChange(record.docId, newAccessType)}
                    disabled={!isAdmin}
                    style={{ width: '100%' }}
                    placeholder="No access"
                    allowClear
                    aria-label={`Access level for ${record.name}`}
                    options={[
                        { value: 'readGroupIds', label: ACCESS_TYPE_LABELS.readGroupIds },
                        { value: 'writeGroupIds', label: ACCESS_TYPE_LABELS.writeGroupIds },
                        { value: 'adminGroupIds', label: ACCESS_TYPE_LABELS.adminGroupIds },
                    ]}
                />
            ),
        },
    ];

    return (
        <FlexCol stretch style={{ width: '100%' }}>
            {contextHolder}
            {!isAdmin && (
                <Alert
                    title="View Only"
                    description="You do not have admin access to modify these settings."
                    type="info"
                    style={{ width: '100%' }}
                />
            )}
            <FlexCol>
                <Text strong>Group Access</Text>
                <Text type="secondary">
                    Assign access levels to security groups. Users in these groups will have the corresponding access to this form.
                </Text>
                <ResponsiveTable<GroupAccessItem>
                    size="small"
                    columns={columns}
                    dataSource={groupAccessList}
                    rowKey="docId"
                    pagination={false}
                    loading={loading}
                    locale={{
                        emptyText: 'No groups available'
                    }}
                />
            </FlexCol>
        </FlexCol>
    );
};

export default FormAccessContent;
